import type { StatusTone } from '@/components/common';

import type { CompanyRegistrationStatus } from './types/companyRegistration.types';

/** Human labels for the backend `CompanyRegistrationStatus` enum. */
export const REGISTRATION_STATUS_LABELS: Record<CompanyRegistrationStatus, string> = {
  PENDING_EMAIL_VERIFICATION: 'Awaiting email verification',
  PENDING_APPROVAL: 'Pending approval',
  APPROVED_AWAITING_BOOTSTRAP: 'Approved (setting up)',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
};

/** Status → StatusBadge tone (one consistent color language — §7.9). */
export const REGISTRATION_STATUS_TONE: Record<CompanyRegistrationStatus, StatusTone> = {
  PENDING_EMAIL_VERIFICATION: 'neutral',
  PENDING_APPROVAL: 'warning',
  APPROVED_AWAITING_BOOTSTRAP: 'info',
  COMPLETED: 'success',
  REJECTED: 'error',
};

/**
 * The admin review tabs → the workflow statuses each one lists.
 *
 * There is no "Expired" tab, because there are no expired rows to put in one: a registration abandoned
 * before its corporate email was verified is deleted by the Worker, not parked in a terminal state
 * (CompanyRegistration.md §3.4). An always-empty tab would suggest a queue a reviewer should be working
 * through and imply the abandoned attempts are still on file.
 */
export const REVIEW_TABS = [
  { label: 'Pending', statuses: ['PENDING_APPROVAL', 'PENDING_EMAIL_VERIFICATION'] },
  { label: 'Approved', statuses: ['APPROVED_AWAITING_BOOTSTRAP', 'COMPLETED'] },
  { label: 'Rejected', statuses: ['REJECTED'] },
] as const satisfies ReadonlyArray<{
  label: string;
  statuses: readonly CompanyRegistrationStatus[];
}>;

/** Client-side cooldown between "resend code" requests, mirroring Identity's verify screen. */
export const RESEND_COOLDOWN_SECONDS = 30;

/**
 * How long an unverified registration survives before the Worker deletes it, as told to the applicant.
 *
 * A duplicate of the server's `CompanyRegistration:VerificationExpiryHours`, and unavoidably so — the
 * wizard states the deadline before any registration exists to carry it, so there is no payload to read
 * it from. Kept as one named constant rather than typed into each screen so the two places that promise
 * it cannot disagree with each other, and so retuning the server has exactly one file to match here.
 */
export const VERIFICATION_WINDOW_HOURS = 24;
